import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Compress as CompressIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useSessionStore } from '../store/sessionStore';
import { useContextStore } from '../store/contextStore';
import { CreateSessionDialog } from '../components/sessions/CreateSessionDialog';
import { SessionStatus } from '../types/session';
import type { CreateSessionRequest } from '../types/session';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * Session list page component.
 */
export const SessionListPage = () => {
  const navigate = useNavigate();
  const {
    sessions,
    sessionsTotalCount,
    loading,
    error,
    loadSessions,
    deleteSession,
    pauseSession,
    resumeSession,
    createSession,
    clearError,
  } = useSessionStore();
  const { showNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SessionStatus | 'all'>('all');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  useEffect(() => {
    loadSessionsData();
  }, [pageNumber, searchTerm, statusFilter, currentTenant?.id, currentProject?.id]);

  const loadSessionsData = async () => {
    try {
      await loadSessions({
        pageNumber,
        pageSize,
        searchTerm: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
    } catch (err: any) {
      showNotification(err.message || 'Failed to load sessions', 'error');
    }
  };

  const handleCreate = async (data: CreateSessionRequest) => {
    try {
      await createSession(data);
      showNotification('Session created successfully', 'success');
      setOpenCreateDialog(false);
      loadSessionsData();
    } catch (err: any) {
      showNotification(err.message || 'Failed to create session', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await deleteSession(id);
      showNotification('Session deleted successfully', 'success');
      loadSessionsData();
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete session', 'error');
    }
  };

  const handlePause = async (id: string) => {
    try {
      await pauseSession(id);
      showNotification('Session paused', 'success');
      loadSessionsData();
    } catch (err: any) {
      showNotification(err.message || 'Failed to pause session', 'error');
    }
  };

  const handleResume = async (id: string) => {
    try {
      await resumeSession(id);
      showNotification('Session resumed', 'success');
      loadSessionsData();
    } catch (err: any) {
      showNotification(err.message || 'Failed to resume session', 'error');
    }
  };

  const getStatusColor = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.Active:
        return 'success';
      case SessionStatus.Paused:
        return 'warning';
      case SessionStatus.Completed:
        return 'info';
      case SessionStatus.Cancelled:
      case SessionStatus.Expired:
      case SessionStatus.Error:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.Active:
        return 'Active';
      case SessionStatus.Paused:
        return 'Paused';
      case SessionStatus.Completed:
        return 'Completed';
      case SessionStatus.Cancelled:
        return 'Cancelled';
      case SessionStatus.Expired:
        return 'Expired';
      case SessionStatus.Error:
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<typeof sessions[0]>[] = [
    {
      id: 'sessionName',
      label: 'Session Name',
      minWidth: 200,
      render: (session) => (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/sessions/${session.id}`)}
        >
          {session.sessionName}
        </Typography>
      ),
    },
    {
      id: 'agentName',
      label: 'Agent',
      minWidth: 150,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (session) => (
        <Chip
          label={getStatusLabel(session.status)}
          color={getStatusColor(session.status) as any}
          size="small"
        />
      ),
    },
    {
      id: 'messageCount',
      label: 'Messages',
      minWidth: 100,
      align: 'right',
    },
    {
      id: 'compaction',
      label: '',
      minWidth: 48,
      align: 'center',
      render: (session) =>
        (session.hasContextSummaries ?? (session.contextSummaryCount ?? 0) > 0) ? (
          <Tooltip title="Chat history compacted">
            <IconButton
              size="small"
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/sessions/${session.id}#context-summaries`);
              }}
              sx={{ cursor: 'pointer' }}
            >
              <CompressIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
    {
      id: 'totalTokensUsed',
      label: 'Tokens',
      minWidth: 120,
      align: 'right',
      render: (session) => session.totalTokensUsed.toLocaleString(),
    },
    {
      id: 'totalCost',
      label: 'Cost',
      minWidth: 100,
      align: 'right',
      render: (session) => `$${session.totalCost.toFixed(4)}`,
    },
    {
      id: 'lastActivity',
      label: 'Last Activity',
      minWidth: 180,
      render: (session) =>
        session.lastActivityAt
          ? new Date(session.lastActivityAt).toLocaleString()
          : new Date(session.createdAt).toLocaleString(),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      render: (session) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => navigate(`/sessions/${session.id}`)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {session.status === SessionStatus.Active ? (
            <Tooltip title="Pause">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePause(session.id);
                }}
                color="warning"
              >
                <PauseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : session.status === SessionStatus.Paused ? (
            <Tooltip title="Resume">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResume(session.id);
                }}
                color="success"
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(session.id);
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageListLayout
        title="Agent Sessions"
        createButton={{ label: 'New Session', onClick: () => setOpenCreateDialog(true), icon: <AddIcon /> }}
        filters={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label="Search sessions"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as SessionStatus | 'all')}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value={SessionStatus.Active}>Active</MenuItem>
                <MenuItem value={SessionStatus.Paused}>Paused</MenuItem>
                <MenuItem value={SessionStatus.Completed}>Completed</MenuItem>
                <MenuItem value={SessionStatus.Cancelled}>Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        }
        loading={loading}
        error={error || undefined}
        onErrorDismiss={clearError}
        isEmpty={sessions.length === 0}
        emptyState={{
          title: 'No sessions found',
          description: 'Create a new session to get started.',
          action: { label: 'New Session', onClick: () => setOpenCreateDialog(true) },
        }}
        pagination={{
          page: pageNumber,
          totalPages: Math.ceil(sessionsTotalCount / pageSize) || 1,
          onChange: (page) => setPageNumber(page),
        }}
      >
        <DataTable columns={tableColumns} rows={sessions} loading={false} />
      </PageListLayout>

      <CreateSessionDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSubmit={handleCreate}
      />
    </>
  );
};
